import { useState, useRef, useEffect } from 'react';
import moment from 'moment';
import clsx from 'clsx';
import { Badge, IconButton, Modal, Dropdown, Menu, Button } from '@components';
import { Calendar } from '@/packs/ui/components/ui/calendar';
import { get } from 'lodash';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useDeal } from '../context';
import { ChevronRight, Settings, X } from 'lucide-react';

const DEADLINES = {
  LATE: 'LATE',
  UPCOMING: 'UPCOMING',
  CURRENT: 'CURRENT',
  UNDEFINED: 'UNDEFINED'
};

const DEADLINES_CLASSES = {
  LATE: {
    bg: 'bg-red-50 hover:bg-red-100',
    text: 'text-red-600'
  },
  CURRENT: {
    bg: 'bg-amber-50 hover:bg-amber-100',
    text: 'text-amber-600'
  },
  UPCOMING: {
    bg: 'bg-slate-50 hover:bg-slate-100',
    text: 'text-slate-600'
  },
  UNDEFINED: {
    bg: 'bg-slate-50',
    text: 'text-slate-600'
  }
};

const deadlineClasses = (date, bypass) =>
  DEADLINES_CLASSES[resolveDeadline(date, bypass) || 'UNDEFINED'];

const resolveDeadline = (date = null, bypass = false) => {
  if (!date || bypass) return;

  const d = moment(date);
  const now = moment();

  if (!d.isValid()) {
    return;
  }

  if (d.isBefore(now.startOf('day'))) return DEADLINES.LATE;

  if (d.isSameOrBefore(now.endOf('week'))) return DEADLINES.CURRENT;

  return DEADLINES.UPCOMING;
};

function EndAtModal({
  currentTaskItem,
  isOpen = false,
  onUpdate = () => {},
  onChange = () => {},
  onClose = () => {}
}) {
  const { dealId, taskItemById, dealObjects } = useDeal();
  const taskItems = Object.values(taskItemById);

  const rawEndAt = currentTaskItem.end_at;
  const momentEndAt = !!rawEndAt ? moment(rawEndAt) : null;
  const endAt = rawEndAt ? momentEndAt.format('DD/MM/YYYY') : null;

  const otherTaskItems = taskItems
    .filter(t => {
      if (!t.end_at) {
        return false;
      }

      if (t.id === currentTaskItem.id) {
        return false;
      }

      if (t.state === 'VALIDATED') {
        return false;
      }

      return moment(t.end_at).isAfter(momentEndAt);
    })
    .filter(Boolean)
    .sort((ta, tb) => moment(ta.end_at).diff(moment(tb.end_at)));

  const formattedInitialDate = currentTaskItem.end_at && moment(currentTaskItem.end_at);

  const initialFutureTaskItemIds = otherTaskItems.map(t => t.id);
  const [selectedTaskItemIds, setSelectedTaskItemIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasFutureDates = otherTaskItems.filter(t => moment(t.end_at) >= momentEndAt).length >= 1;

  const mainObjects = get(dealObjects, get(currentTaskItem, 'target_type'), []);
  const mainTrackable = mainObjects.find(
    object => Number(object.id) === Number(currentTaskItem.target_id)
  );
  const mainTrackableLabel = get(mainTrackable, 'label');
  const mainTrackableSubtitle = get(mainTrackable, 'subtitle');

  const [date, setDate] = useState(formattedInitialDate);
  const numOfDaysToDiff = formattedInitialDate
    ? Math.ceil(date.clone().diff(formattedInitialDate, 'hours') / 24)
    : 0;
  const isSame = date && formattedInitialDate ? date.clone().isSame(formattedInitialDate) : true;
  const indeterminate =
    selectedTaskItemIds.length >= 1 && otherTaskItems.length > selectedTaskItemIds.length;
  const ref = useRef();

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [indeterminate]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedTaskItemIds([]);
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const recapComponent = (
    <div className="bg-blue-50 border border-blue-200 text-blue-600 rounded-lg py-2 px-4 flex flex-col text-sm">
      <span className="grid grid-cols-[200px_1fr] w-full">
        <span className="font-hmedium">Date initiale</span>
        {formattedInitialDate && (
          <span className="text-right">{formattedInitialDate.format('DD/MM/YYYY')}</span>
        )}
      </span>
      <span className="grid grid-cols-[200px_1fr] w-full">
        <span className="font-hmedium">Date mise à jour</span>
        {date && <span className="text-right">{date.format('DD/MM/YYYY')}</span>}
      </span>
      <span className="grid grid-cols-[200px_1fr] w-full">
        <span className="font-hmedium">Différence</span>
        <span className="text-right">
          {numOfDaysToDiff > 0 ? `+${numOfDaysToDiff}` : numOfDaysToDiff} jours
        </span>
      </span>
    </div>
  );

  const dateComponent = (
    <div className="flex flex-col space-y-6">
      <div className="flex flex-col bg-slate-50 py-3 px-6 border-b sticky top-0 z-[2]">
        <h4 className="font-hmedium tracking-tight w-full min-w-0 truncate">
          Choisir une nouvelle date
        </h4>
        <p className="text-xs text-gray-500">
          Avancez ou reportez la date d'échéance
          <br /> de cette démarche.
        </p>
      </div>
      <div className="[&_.rdp]:!m-0 [&_.rdp]:border [&_.rdp]:border-slate-200 [&_.rdp]:rounded-lg [&_.rdp]:p-3 px-6 relative z-[1]">
        <Calendar
          mode="single"
          selected={date ? date.toDate() : undefined}
          onSelect={d => {
            if (d) setDate(moment(d));
          }}
        />
      </div>
      {date && formattedInitialDate && (
        <div className="px-6 pb-6 relative z-[1]">{recapComponent}</div>
      )}
    </div>
  );

  const futureDatesList = (
    <div
      className={clsx(`relative h-full flex flex-col`, isSame && 'opacity-50 pointer-events-none')}
    >
      <div className="flex-shrink-0">
        <div className="flex flex-col bg-slate-50 py-3 px-6 border-b">
          <h4 className="font-hmedium tracking-tight w-full min-w-0 truncate">
            {numOfDaysToDiff >= 0 ? 'Repousser' : 'Avancer'} d'autres échéances{' '}
            {numOfDaysToDiff === 1
              ? 'de 1 jour'
              : numOfDaysToDiff > 1
                ? `de ${numOfDaysToDiff} jours`
                : ''}{' '}
            ?
          </h4>
          <p className="text-xs text-gray-500">
            Modifiez les échéances d'autres démarches
            <br /> dans les mêmes proportions.
          </p>
        </div>
        <button
          onClick={() => {
            if (otherTaskItems.length === selectedTaskItemIds.length) {
              setSelectedTaskItemIds([]);
            } else {
              setSelectedTaskItemIds(otherTaskItems.map(t => t.id));
            }
          }}
          className="flex space-x-2 min-w-0 truncate w-full py-1 px-6 bg-white border-b items-center cursor-pointer hover:bg-slate-50 cursor-pointer"
        >
          <div className="flex-shrink-0 -mt-1">
            <input
              type="checkbox"
              readOnly
              ref={ref}
              checked={otherTaskItems.length === selectedTaskItemIds.length}
            />
          </div>
          <div className="flex-shrink flex flex-col min-w-0 truncate w-full text-xs font-hmedium">
            Tout sélectionner
          </div>
        </button>
      </div>
      <div className="z-[1] py-4 overflow-y-scroll h-full">
        {otherTaskItems.map(ti => {
          const momentDate = moment(ti.end_at);
          const newDate = momentDate.clone().add(numOfDaysToDiff, 'days');
          const checked = selectedTaskItemIds.includes(ti.id);
          const objects = get(dealObjects, get(ti, 'target_type'), []);
          const trackable = objects.find(object => Number(object.id) === Number(ti.target_id));
          const trackableLabel = get(trackable, 'label');
          const trackableSubtitle = get(trackable, 'subtitle');

          return (
            <button
              type="button"
              onClick={() =>
                setSelectedTaskItemIds(prev => {
                  if (prev.includes(ti.id)) {
                    return prev.filter(id => id !== ti.id);
                  } else {
                    return [...prev, ti.id];
                  }
                })
              }
              className={clsx('flex space-x-2 min-w-0 truncate w-full py-1 px-6 h-[56px]')}
            >
              <div className="flex-shrink-0 -mt-1">
                <input type="checkbox" readOnly checked={checked} />
              </div>
              <div className="flex-shrink flex flex-col min-w-0 truncate w-full">
                <span className="font-hmedium tracking-tight min-w-0 truncate text-sm mb-1">
                  {[ti.label, trackableLabel, trackableSubtitle].filter(Boolean).join(' - ')}
                </span>

                <span className="flex items-center space-x-1">
                  <span>
                    <Badge label={momentDate.format('DD/MM/YYYY')} />
                  </span>
                  {numOfDaysToDiff !== 0 && selectedTaskItemIds.includes(ti.id) && (
                    <>
                      <span>
                        <ChevronRight className="w-4 h-4" />
                      </span>
                      <span>
                        <Badge color="blue" label={newDate.format('DD/MM/YYYY')} />
                      </span>
                    </>
                  )}
                </span>
              </div>
            </button>
          );
        })}
      </div>
      <div className="flex-shrink-0">
        <div className="bg-slate-50 py-3 px-6 z-[2] border-t text-xs text-slate-500">
          <span>
            Seules les démarches qui ont une échéance future et qui ne sont pas terminées peuvent
            être sélectionnées.
          </span>
        </div>
      </div>
    </div>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div
        className={clsx(
          'w-full rounded-lg bg-white overflow-hidden relative flex flex-col max-w-[90vw] max-h-[90vh]',
          hasFutureDates && date && formattedInitialDate ? '!w-[800px]' : '!w-[400px]'
        )}
      >
        <div className="sticky top-0 flex-shrink-0 w-full">
          <div className="py-3 px-6 flex items-center justify-between border-b">
            <div className="flex flex-col truncate min-w-à w-full">
              <h3 className="font-hmedium tracking-tight text-xl">Modifier la date d'échéance</h3>
              <p className="text-slate-400 flex items-center space-x-2 truncate min-w-0">
                <span className="flex-shrink-0">{currentTaskItem.label}</span>
                {(mainTrackable || mainTrackableSubtitle) && (
                  <Badge
                    className="flex-shrink min-w-0 truncate [&_span]:truncate"
                    label={[mainTrackableLabel, mainTrackableSubtitle].filter(Boolean).join(' - ')}
                  />
                )}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <IconButton icon={XMarkIcon} onClick={onClose} size="lg" />
            </div>
          </div>
        </div>

        {formattedInitialDate && hasFutureDates ? (
          <div className="lg:flex w-full h-full min-h-0 divide-x overflow-y-scroll lg:overflow-hidden">
            <div className="overflow-y-scroll w-1/2">{dateComponent}</div>
            <div className="w-1/2 flex-shrink">{futureDatesList}</div>
          </div>
        ) : (
          dateComponent
        )}

        <div className="flex justify-end items-center border-t py-2 px-6 items-center ">
          <span className="text-xs flex mr-2">
            {numOfDaysToDiff !== 0 && (
              <span className="flex w-full text-blue-600 font-hmedium">
                {selectedTaskItemIds.length + 1 > 1
                  ? `Les échéances sélectionnées seront ${numOfDaysToDiff >= 0 ? 'repoussées' : 'avancées'}`
                  : `L'échéance sélectionnée sera ${numOfDaysToDiff >= 0 ? 'repoussée' : 'avancée'}`}{' '}
                de {Math.abs(numOfDaysToDiff)} {Math.abs(numOfDaysToDiff) > 1 ? 'jours.' : 'jour.'}
              </span>
            )}
          </span>
          <Button
            disabled={date && formattedInitialDate && isSame}
            label={
              selectedTaskItemIds.length + 1 > 1
                ? `Mettre à jour ${selectedTaskItemIds.length + 1} échéances`
                : 'Mettre à jour 1 échéance'
            }
            color="blue"
            onClick={() => {
              let payload = { end_at: date.format('DD/MM/YYYY') };
              if (numOfDaysToDiff !== 0 && selectedTaskItemIds.length > 0) {
                payload = {
                  ...payload,
                  diff: {
                    num_of_days: numOfDaysToDiff,
                    task_item_ids: selectedTaskItemIds
                  }
                };
              }

              try {
                onUpdate(payload);
              } catch (err) {
                console.log(err);
              } finally {
                onClose();
              }
            }}
            icon={ChevronRight}
          />
        </div>
      </div>
    </Modal>
  );
}

function CellWithModal({ taskItem, isOpen, setIsOpened, children, onUpdate }) {
  const [modalIsOpened, setModalIsOpened] = useState(false);

  return (
    <>
      <Dropdown isOpened={isOpen} setIsOpened={setIsOpened} button={children}>
        <Menu
          className="w-[200px] flex flex-col"
          groups={[
            {
              options: [
                {
                  icon: Settings,
                  label: 'Modifier la date',
                  onClick: () => setModalIsOpened(true)
                },
                {
                  icon: X,
                  label: 'Supprimer la date',
                  onClick: () => {
                    onUpdate({ end_at: '' });
                    setModalIsOpened(false);
                    setIsOpened(false);
                  }
                }
              ]
            }
          ]}
        />
      </Dropdown>

      <EndAtModal
        currentTaskItem={taskItem}
        isOpen={modalIsOpened}
        onClose={() => setModalIsOpened(false)}
        onUpdate={onUpdate}
      />
    </>
  );
}

function BasicCell({ taskItem, isOpen, setIsOpened, onUpdate, children }) {
  return (
    <Dropdown isOpened={isOpen} setIsOpened={setIsOpened} button={children}>
      <Calendar
        mode="single"
        selected={taskItem.end_at ? moment(taskItem.end_at).toDate() : undefined}
        onSelect={d => {
          if (d) {
            onUpdate({ end_at: moment(d).format('DD/MM/YYYY') });
            setIsOpened(false);
          }
        }}
      />
    </Dropdown>
  );
}

export default function EndAtColumn({ taskItem, onUpdate }) {
  const [dateIsOpened, setDateIsOpened] = useState(false);
  const { dealId } = useDeal();

  const rawEndAt = get(taskItem, 'end_at');
  const momentEndAt = !!rawEndAt ? moment(rawEndAt) : null;
  const endAt = rawEndAt ? momentEndAt.format('D/M/YY') : null;
  const { bg: deadlineBgClasses, text: deadlineTextClasses } = rawEndAt
    ? deadlineClasses(rawEndAt, get(taskItem, 'state') === 'VALIDATED')
    : {};

  const displayedDate = !!rawEndAt ? endAt : '__/__/__';

  const cellContent = (
    <button onClick={() => setDateIsOpened(true)} className={deadlineTextClasses}>
      {displayedDate}
    </button>
  );

  const classes = clsx(
    '!p-0',
    '[&>div]:w-full',
    '[&>div]:flex',
    '[&>div]:justify-center',
    '[&>div]:items-center',
    'cell',
    'inline-flex',
    'justify-center',
    'items-center',
    'text-xs',
    'font-hmedium',
    'text-slate-500',
    deadlineBgClasses
  );

  return (
    <>
      <div className={classes}>
        {!!rawEndAt ? (
          <CellWithModal
            taskItem={taskItem}
            isOpen={dateIsOpened}
            setIsOpened={setDateIsOpened}
            onUpdate={onUpdate}
          >
            {cellContent}
          </CellWithModal>
        ) : (
          <BasicCell
            isOpen={dateIsOpened}
            setIsOpened={setDateIsOpened}
            taskItem={taskItem}
            onUpdate={onUpdate}
          >
            {cellContent}
          </BasicCell>
        )}
      </div>
    </>
  );
}
